import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Ban, CalendarClock, Eye, History, MessageCircle, Plus, RefreshCw, Search, X } from 'lucide-react'
import { api, getApiErrorMessage } from '../lib/api'
import { formatMenuInstant } from '../lib/date'
import { useToast } from '../ui/ToastProvider'

const TARGET_SELECTED = 'SELECTED_USERS'
const TARGET_ALL_KAKAO = 'ALL_KAKAO_CONSENT_USERS'
const PURPOSE_SERVICE_NOTICE = 'SERVICE_NOTICE'
const PURPOSE_MARKETING = 'MARKETING'
const DEFAULT_TEMPLATE_CODE = 'INSURER_NEWSLETTER_MONTHLY_BRIEF'
const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'SCHEDULED', label: '예약 대기' },
  { value: 'SENDING', label: '발송 중' },
  { value: 'SENT', label: '발송 완료' },
  { value: 'PARTIAL_FAILED', label: '일부 실패' },
  { value: 'FAILED', label: '발송 실패' },
  { value: 'CANCELLED', label: '예약 취소' },
]

function unwrap(res, fallback) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : (res?.data ?? fallback)
}

function orgLabel(org) {
  return org?.displayName || org?.name || org?.orgName || '조직'
}

function pad(n) {
  return String(n).padStart(2, '0')
}

function datetimeLocalValue(date = new Date()) {
  const d = new Date(date)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function defaultScheduleTime() {
  const d = new Date(Date.now() + 60 * 60 * 1000)
  d.setSeconds(0, 0)
  return datetimeLocalValue(d)
}

function monthInputValue(date = new Date()) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`
}

function parseMonth(value) {
  const [year, month] = String(value || '').split('-').map(Number)
  return { year, month }
}

function scheduledIso(value) {
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

function parseJson(raw) {
  if (!raw) return null
  try {
    return JSON.parse(String(raw))
  } catch (_) {
    return null
  }
}

function addName(out, raw) {
  const value = String(raw || '').trim()
  if (value && !out.includes(value)) out.push(value)
}

function variableNames(raw) {
  const parsed = parseJson(raw)
  const out = []
  if (Array.isArray(parsed)) {
    parsed.forEach((item) => addName(out, item))
  } else if (parsed && typeof parsed === 'object') {
    const props = parsed.properties && typeof parsed.properties === 'object' ? parsed.properties : parsed
    Object.keys(props).forEach((key) => addName(out, key))
  }
  return out
}

function fallbackVariableNames(templateCode) {
  return String(templateCode || '').toUpperCase() === DEFAULT_TEMPLATE_CODE
    ? ['title', 'monthLabel', 'lifeSummary', 'fireSummary', 'nonlifeSummary', 'message', 'summaryUrl']
    : []
}

function buildVariableFields(template) {
  if (!template) return []
  const required = variableNames(template.requiredVariables)
  const optional = variableNames(template.optionalVariables)
  const schema = parseJson(template.variableSchema)
  const props = schema?.properties && typeof schema.properties === 'object' ? schema.properties : {}
  const names = []
  required.forEach((name) => addName(names, name))
  optional.forEach((name) => addName(names, name))
  Object.keys(props).forEach((name) => addName(names, name))
  if (!names.length) fallbackVariableNames(template.templateCode).forEach((name) => addName(names, name))
  return names.map((name) => {
    const meta = props[name] || {}
    const lower = name.toLowerCase()
    return {
      name,
      label: meta.title || name,
      required: required.includes(name),
      multiline: meta.format === 'textarea' || lower.includes('message') || lower.includes('summary') || lower.includes('content'),
    }
  })
}

function variableValue(variables, name) {
  return variables?.[name] == null ? '' : String(variables[name])
}

function cleanVariables(variables) {
  const out = {}
  Object.entries(variables || {}).forEach(([key, value]) => {
    const k = String(key || '').trim()
    const v = String(value ?? '').trim()
    if (k && v) out[k] = v
  })
  return out
}

function briefText(payload, key) {
  const value = payload?.[key]
  if (!value) return ''
  if (typeof value === 'string') return value
  return value.issuesSummary || value.summary || value.content || ''
}

function buildPreview(template, variables) {
  const cleaned = cleanVariables(variables)
  const direct = cleaned.message || cleaned.content || cleaned.summary
  if (direct) return direct
  const lines = [`템플릿: ${template?.templateName || template?.templateCode || '-'}`]
  Object.entries(cleaned).forEach(([key, value]) => lines.push(`${key}: ${value}`))
  return lines.join('\n')
}

function statusClass(status) {
  if (status === 'SCHEDULED') return 'bg-sky-50 text-sky-700 border-sky-200'
  if (status === 'SENT') return 'bg-emerald-50 text-emerald-700 border-emerald-200'
  if (status === 'PARTIAL_FAILED') return 'bg-amber-50 text-amber-700 border-amber-200'
  if (status === 'FAILED') return 'bg-rose-50 text-rose-700 border-rose-200'
  if (status === 'CANCELLED') return 'bg-slate-100 text-slate-600 border-slate-200'
  return 'bg-indigo-50 text-indigo-700 border-indigo-200'
}

export default function OperatorAlimtalkSchedulerPage() {
  const { show } = useToast()
  const [activeTab, setActiveTab] = useState('create')
  const [templates, setTemplates] = useState([])
  const [templateCode, setTemplateCode] = useState('')
  const [variables, setVariables] = useState({})
  const [customKey, setCustomKey] = useState('')
  const [customValue, setCustomValue] = useState('')
  const [briefMonth, setBriefMonth] = useState(monthInputValue())
  const [scheduledAt, setScheduledAt] = useState(defaultScheduleTime)
  const [targetMode, setTargetMode] = useState(TARGET_SELECTED)
  const [confirmed, setConfirmed] = useState(false)
  const [messagePurpose, setMessagePurpose] = useState(PURPOSE_SERVICE_NOTICE)
  const [query, setQuery] = useState('')
  const [candidates, setCandidates] = useState([])
  const [selected, setSelected] = useState([])
  const [orgOptions, setOrgOptions] = useState([])
  const [orgQuery, setOrgQuery] = useState('')
  const [selectedOrgId, setSelectedOrgId] = useState('')
  const [loading, setLoading] = useState(false)
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [briefLoading, setBriefLoading] = useState(false)
  const [orgSelecting, setOrgSelecting] = useState(false)
  const [orgsLoading, setOrgsLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyStatus, setHistoryStatus] = useState('')
  const [historyPage, setHistoryPage] = useState(0)
  const [historyResult, setHistoryResult] = useState({ items: [], total: 0, totalPages: 0 })
  const [detailRow, setDetailRow] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [cancellingId, setCancellingId] = useState(null)

  const selectedTemplate = useMemo(
    () => templates.find((item) => item.templateCode === templateCode) || null,
    [templates, templateCode],
  )
  const variableFields = useMemo(() => buildVariableFields(selectedTemplate), [selectedTemplate])
  const selectedIds = useMemo(() => new Set(selected.map((item) => item.userId)), [selected])
  const availableCandidates = useMemo(
    () => candidates.filter((item) => !selectedIds.has(item.userId)),
    [candidates, selectedIds],
  )
  const selectedOrg = useMemo(
    () => orgOptions.find((org) => String(org.id) === String(selectedOrgId)) || null,
    [orgOptions, selectedOrgId],
  )
  const filteredOrgOptions = useMemo(() => {
    const q = orgQuery.trim().toLowerCase()
    const base = Array.isArray(orgOptions) ? orgOptions : []
    const filtered = q ? base.filter((org) => String(orgLabel(org)).toLowerCase().includes(q)) : base
    const limited = filtered.slice(0, 120)
    const current = selectedOrgId ? base.find((org) => String(org.id) === String(selectedOrgId)) : null
    if (current && !limited.some((org) => String(org.id) === String(current.id))) return [current, ...limited]
    return limited
  }, [orgOptions, orgQuery, selectedOrgId])

  const requiredReady = variableFields.every((field) => !field.required || variableValue(variables, field.name).trim())
  const canCreate = Boolean(templateCode
    && scheduledIso(scheduledAt)
    && requiredReady
    && (targetMode === TARGET_ALL_KAKAO || selected.length > 0)
    && (targetMode !== TARGET_ALL_KAKAO || confirmed)
    && !creating)

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true)
    try {
      const res = await api.get('/operator/alimtalk-sends/templates', { __silent: true })
      const rows = unwrap(res, [])
      const list = Array.isArray(rows) ? rows : []
      setTemplates(list)
      const preferred = list.find((item) => item.templateCode === DEFAULT_TEMPLATE_CODE) || list[0]
      setTemplateCode((prev) => prev || preferred?.templateCode || '')
    } catch (_) {
      setTemplates([])
    } finally {
      setTemplatesLoading(false)
    }
  }, [])

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const params = { page: historyPage, size: 20 }
      if (historyStatus) params.status = historyStatus
      const res = await api.get('/operator/alimtalk-sends/schedules', { params, __silent: true })
      const payload = unwrap(res, {})
      setHistoryResult({
        items: Array.isArray(payload.items) ? payload.items : [],
        total: Number(payload.total || 0),
        totalPages: Number(payload.totalPages || 0),
      })
    } catch (error) {
      setHistoryResult({ items: [], total: 0, totalPages: 0 })
      show(getApiErrorMessage(error, '예약발송 이력을 불러오지 못했습니다.'), 'error')
    } finally {
      setHistoryLoading(false)
    }
  }, [historyPage, historyStatus, show])

  useEffect(() => { void loadTemplates() }, [loadTemplates])

  useEffect(() => {
    let cancelled = false
    async function loadOrgs() {
      setOrgsLoading(true)
      try {
        const res = await api.get('/orgs/options', { params: { scope: 'all' }, __silent: true })
        const payload = unwrap(res, [])
        const rows = Array.isArray(payload) ? payload : []
        const sorted = [...rows].sort((a, b) => String(orgLabel(a)).localeCompare(String(orgLabel(b)), 'ko'))
        if (!cancelled) setOrgOptions(sorted)
      } catch (_) {
        if (!cancelled) setOrgOptions([])
      } finally {
        if (!cancelled) setOrgsLoading(false)
      }
    }
    loadOrgs()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    setVariables((prev) => {
      const next = { ...prev }
      variableFields.forEach((field) => {
        if (next[field.name] == null) next[field.name] = ''
      })
      return next
    })
  }, [variableFields])

  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const params = {}
        if (query.trim()) params.q = query.trim()
        if (selectedOrgId) params.orgId = Number(selectedOrgId)
        const res = await api.get('/operator/alimtalk-sends/recipients', { params, __silent: true })
        const payload = unwrap(res, [])
        if (!cancelled) setCandidates(Array.isArray(payload) ? payload : [])
      } catch (_) {
        if (!cancelled) setCandidates([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query, selectedOrgId])

  useEffect(() => {
    if (activeTab === 'history') void loadHistory()
  }, [activeTab, loadHistory])

  const setVariable = (name, value) => {
    setVariables((prev) => ({ ...prev, [name]: value }))
  }

  const addCustomVariable = () => {
    const key = customKey.trim()
    if (!key) {
      show('변수명을 입력해 주세요.', 'error')
      return
    }
    setVariables((prev) => ({ ...prev, [key]: customValue }))
    setCustomKey('')
    setCustomValue('')
  }

  const removeVariable = (name) => {
    setVariables((prev) => {
      const next = { ...prev }
      delete next[name]
      return next
    })
  }

  const addRecipient = (item) => {
    if (!item || selectedIds.has(item.userId)) return
    setSelected((prev) => [...prev, item])
  }

  const addRecipients = (items) => {
    const rows = Array.isArray(items) ? items : []
    setSelected((prev) => {
      const seen = new Set(prev.map((item) => item.userId))
      const next = [...prev]
      for (const item of rows) {
        if (!item?.userId || seen.has(item.userId)) continue
        seen.add(item.userId)
        next.push(item)
      }
      return next
    })
  }

  const removeRecipient = (userId) => {
    setSelected((prev) => prev.filter((item) => item.userId !== userId))
  }

  const loadMonthlyBrief = async () => {
    const { year, month } = parseMonth(briefMonth)
    if (!year || !month) {
      show('대상 월을 확인해 주세요.', 'error')
      return
    }
    setBriefLoading(true)
    try {
      const res = await api.get('/insurer-newsletter-monthly-briefs', { params: { year, month }, __silent: true })
      const data = unwrap(res, null)
      if (!data?.payload) {
        show('해당 월의 월간 브리프가 없습니다.', 'error')
        return
      }
      const life = briefText(data.payload, 'lifeBrief')
      const fire = briefText(data.payload, 'fireBrief')
      setVariables((prev) => ({
        ...prev,
        title: prev.title || `${year}년 ${month}월 보험사 소식지 요약`,
        monthLabel: `${year}년 ${month}월`,
        lifeSummary: life,
        fireSummary: fire,
        nonlifeSummary: fire,
        message: prev.message || `${year}년 ${month}월 보험사 소식지 업계 동향을 확인해 주세요.`,
        summaryUrl: `/work/insurer-newsletters?year=${year}&month=${month}`,
      }))
      show('월간 브리프를 불러왔습니다.', 'success')
    } catch (error) {
      show(getApiErrorMessage(error, '월간 브리프를 불러오지 못했습니다.'), 'error')
    } finally {
      setBriefLoading(false)
    }
  }

  const selectOrgRecipients = async () => {
    if (!selectedOrgId) {
      show('조직을 선택해 주세요.', 'error')
      return
    }
    setOrgSelecting(true)
    try {
      const res = await api.get('/operator/alimtalk-sends/recipients', {
        params: { orgId: Number(selectedOrgId) },
        __silent: true,
      })
      const rows = unwrap(res, [])
      const list = Array.isArray(rows) ? rows : []
      setCandidates(list)
      addRecipients(list)
      show(`${orgLabel(selectedOrg)} 사용자를 선택 목록에 추가했습니다.`, 'success')
    } catch (error) {
      show(getApiErrorMessage(error, '조직 사용자 목록을 불러오지 못했습니다.'), 'error')
    } finally {
      setOrgSelecting(false)
    }
  }

  const createSchedule = async (event) => {
    event.preventDefault()
    if (!canCreate) {
      show('예약발송 값을 확인해 주세요.', 'error')
      return
    }
    setCreating(true)
    try {
      const payload = {
        targetMode,
        userIds: targetMode === TARGET_SELECTED ? selected.map((item) => item.userId) : [],
        confirmed,
        messagePurpose,
        templateCode,
        scheduledAt: scheduledIso(scheduledAt),
        variables: cleanVariables(variables),
      }
      await api.post('/operator/alimtalk-sends/schedules', payload)
      show('예약발송이 생성되었습니다.', 'success')
      setActiveTab('history')
      setHistoryPage(0)
      await loadHistory()
    } catch (error) {
      show(getApiErrorMessage(error, '예약발송 생성에 실패했습니다.'), 'error')
    } finally {
      setCreating(false)
    }
  }

  const openDetail = async (row) => {
    if (!row?.id) return
    setDetailLoading(true)
    try {
      const res = await api.get(`/operator/alimtalk-sends/schedules/${row.id}`, { __silent: true })
      setDetailRow(unwrap(res, null))
    } catch (error) {
      show(getApiErrorMessage(error, '예약발송 상세를 불러오지 못했습니다.'), 'error')
    } finally {
      setDetailLoading(false)
    }
  }

  const cancelSchedule = async (row) => {
    if (!row?.id) return
    if (!window.confirm('선택한 예약발송을 취소할까요?')) return
    setCancellingId(row.id)
    try {
      const res = await api.post(`/operator/alimtalk-sends/schedules/${row.id}/cancel`)
      const data = unwrap(res, null)
      show('예약발송이 취소되었습니다.', 'success')
      if (detailRow?.id === row.id) setDetailRow(data)
      await loadHistory()
    } catch (error) {
      show(getApiErrorMessage(error, '예약발송 취소에 실패했습니다.'), 'error')
    } finally {
      setCancellingId(null)
    }
  }

  const previewText = buildPreview(selectedTemplate, variables)
  const customVariableNames = Object.keys(variables).filter((name) => !variableFields.some((field) => field.name === name))
  const isNewsletterTemplate = String(templateCode || '').toUpperCase() === DEFAULT_TEMPLATE_CODE

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-950">알림톡발송</h1>
        <div className="inline-flex rounded border border-slate-200 bg-white p-1">
          <button
            type="button"
            onClick={() => setActiveTab('create')}
            className={`inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm font-semibold ${activeTab === 'create' ? 'bg-brand-navy text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <CalendarClock className="h-4 w-4" />
            예약발송생성
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className={`inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm font-semibold ${activeTab === 'history' ? 'bg-brand-navy text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <History className="h-4 w-4" />
            이력
          </button>
        </div>
      </div>

      {activeTab === 'create' ? (
        <form onSubmit={createSchedule} className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-5">
            <div className="rounded border border-slate-200 bg-white p-5">
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px_180px]">
                <label className="block">
                  <span className="mb-1 block text-sm font-semibold text-slate-700">활성 템플릿</span>
                  <select
                    value={templateCode}
                    onChange={(e) => setTemplateCode(e.target.value)}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                  >
                    <option value="">{templatesLoading ? '템플릿 불러오는 중' : '템플릿 선택'}</option>
                    {templates.map((template) => (
                      <option key={template.templateCode} value={template.templateCode}>
                        {template.templateName || template.templateCode} ({template.templateCode})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm font-semibold text-slate-700">발송시간</span>
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                  />
                </label>                <label className="block">
                  <span className="mb-1 block text-sm font-semibold text-slate-700">발송 목적</span>
                  <select
                    value={messagePurpose}
                    onChange={(e) => setMessagePurpose(e.target.value)}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                  >
                    <option value={PURPOSE_SERVICE_NOTICE}>서비스 안내</option>
                    <option value={PURPOSE_MARKETING}>마케팅·광고</option>
                  </select>
                </label>
              </div>

              {isNewsletterTemplate ? (
                <div className="mt-4 grid gap-3 lg:grid-cols-[180px_auto] lg:items-end">
                  <label className="block">
                    <span className="mb-1 block text-sm font-semibold text-slate-700">소식지 월</span>
                    <input
                      type="month"
                      value={briefMonth}
                      onChange={(e) => setBriefMonth(e.target.value)}
                      className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={loadMonthlyBrief}
                    disabled={briefLoading}
                    className="inline-flex items-center justify-center gap-2 rounded border border-brand-navy px-3 py-2 text-sm font-semibold text-brand-navy hover:bg-sky-50 disabled:border-slate-200 disabled:text-slate-400 disabled:hover:bg-transparent"
                  >
                    <RefreshCw className={`h-4 w-4 ${briefLoading ? 'animate-spin' : ''}`} />
                    브리프 불러오기
                  </button>
                </div>
              ) : null}
            </div>

            <div className="rounded border border-slate-200 bg-white p-5">
              <div className="mb-4 text-sm font-semibold text-slate-800">변수 입력</div>
              <div className="grid gap-4 lg:grid-cols-2">
                {variableFields.map((field) => (
                  <label key={field.name} className="block">
                    <span className="mb-1 block text-sm font-semibold text-slate-700">
                      {field.label}{field.required ? ' *' : ''}
                    </span>
                    {field.multiline ? (
                      <textarea
                        value={variableValue(variables, field.name)}
                        onChange={(e) => setVariable(field.name, e.target.value)}
                        rows={6}
                        className="w-full resize-y rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                      />
                    ) : (
                      <input
                        value={variableValue(variables, field.name)}
                        onChange={(e) => setVariable(field.name, e.target.value)}
                        className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                      />
                    )}
                  </label>
                ))}
              </div>

              <div className="mt-4 grid gap-2 lg:grid-cols-[180px_minmax(0,1fr)_auto]">
                <input
                  value={customKey}
                  onChange={(e) => setCustomKey(e.target.value)}
                  placeholder="변수명"
                  className="rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                />
                <input
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  placeholder="값"
                  className="rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                />
                <button
                  type="button"
                  onClick={addCustomVariable}
                  className="inline-flex items-center justify-center gap-2 rounded border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <Plus className="h-4 w-4" />
                  변수 추가
                </button>
              </div>
              {customVariableNames.length ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {customVariableNames.map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => removeVariable(name)}
                      className="inline-flex max-w-full items-center gap-1 rounded-full border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                    >
                      <span className="truncate">{name}</span>
                      <X className="h-3 w-3" />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="rounded border border-slate-200 bg-white p-5">
              <div className="mb-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  className={`rounded border px-3 py-2 text-sm font-semibold ${targetMode === TARGET_SELECTED ? 'border-brand-navy bg-sky-50 text-brand-navy' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                  onClick={() => setTargetMode(TARGET_SELECTED)}
                >
                  선택 사용자
                </button>
                <button
                  type="button"
                  className={`rounded border px-3 py-2 text-sm font-semibold ${targetMode === TARGET_ALL_KAKAO ? 'border-brand-navy bg-sky-50 text-brand-navy' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                  onClick={() => setTargetMode(TARGET_ALL_KAKAO)}
                >
                  수신동의 사용자 전체
                </button>
              </div>

              {targetMode === TARGET_SELECTED ? (
                <div className="space-y-4">
                  <div className="grid gap-3 lg:grid-cols-[minmax(220px,320px)_minmax(0,1fr)]">
                    <label className="block">
                      <span className="mb-1 block text-sm font-semibold text-slate-700">조직 검색</span>
                      <input
                        value={orgQuery}
                        onChange={(e) => setOrgQuery(e.target.value)}
                        placeholder="조직명"
                        className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-sm font-semibold text-slate-700">조직 선택</span>
                      <select
                        value={selectedOrgId}
                        onChange={(e) => {
                          setSelectedOrgId(e.target.value)
                          setQuery('')
                        }}
                        className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                      >
                        <option value="">{orgsLoading ? '조직 불러오는 중' : '전체 조직'}</option>
                        {filteredOrgOptions.map((org) => (
                          <option key={org.id} value={org.id}>{orgLabel(org)}</option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
                    <div>
                      <div className="flex flex-wrap items-end gap-2">
                        <label className="relative block min-w-[220px] flex-1">
                          <span className="mb-1 block text-sm font-semibold text-slate-700">사용자 목록</span>
                          <Search className="pointer-events-none absolute left-3 top-[34px] h-4 w-4 text-slate-400" />
                          <input
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="이름 또는 이메일"
                            className="w-full rounded border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={selectOrgRecipients}
                          disabled={!selectedOrgId || orgSelecting}
                          className="rounded border border-brand-navy px-3 py-2 text-sm font-semibold text-brand-navy hover:bg-sky-50 disabled:border-slate-200 disabled:text-slate-400 disabled:hover:bg-transparent"
                        >
                          {orgSelecting ? '선택 중' : '조직 전체 선택'}
                        </button>
                      </div>
                      <div className="mt-3 max-h-[360px] overflow-auto rounded border border-slate-200">
                        {loading ? (
                          <div className="px-3 py-6 text-center text-sm text-slate-500">검색 중</div>
                        ) : availableCandidates.length ? (
                          availableCandidates.map((item) => (
                            <button
                              key={item.userId}
                              type="button"
                              onClick={() => addRecipient(item)}
                              className="flex w-full items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-slate-50"
                            >
                              <span className="min-w-0">
                                <span className="block truncate font-semibold text-slate-900">{item.name || item.email}</span>
                                <span className="block truncate text-xs text-slate-500">{item.email} · {item.orgName || '-'}</span>
                              </span>
                              <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                                {item.phoneMasked || '번호 있음'}
                              </span>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-6 text-center text-sm text-slate-500">검색 결과가 없습니다.</div>
                        )}
                      </div>
                    </div>

                    <div className="rounded border border-slate-200">
                      <div className="border-b border-slate-200 px-3 py-2 text-sm font-semibold text-slate-800">
                        선택 {selected.length.toLocaleString()}명
                      </div>
                      <div className="max-h-[360px] overflow-auto">
                        {selected.length ? selected.map((item) => (
                          <div key={item.userId} className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 text-sm last:border-b-0">
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-semibold text-slate-900">{item.name || item.email}</div>
                              <div className="truncate text-xs text-slate-500">{item.orgName || '-'} · {item.phoneMasked || '번호 있음'}</div>
                            </div>
                            <button
                              type="button"
                              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                              aria-label="대상 제거"
                              onClick={() => removeRecipient(item.userId)}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        )) : (
                          <div className="px-3 py-6 text-center text-sm text-slate-500">선택한 사용자가 없습니다.</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <label className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>휴대폰 번호가 있고 카카오 알림톡 수신에 동의한 모든 활성 사용자에게 예약합니다.</span>
                </label>
              )}
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded border border-slate-200 bg-white p-4">
              <button
                type="submit"
                disabled={!canCreate}
                className="inline-flex w-full items-center justify-center gap-2 rounded bg-brand-navy px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-navy/90 disabled:opacity-50"
              >
                <CalendarClock className="h-4 w-4" />
                {creating ? '생성 중' : '예약발송 생성'}
              </button>
            </div>

            <div className="rounded border border-slate-200 bg-white p-4 text-sm">
              <div className="mb-2 flex items-center gap-2 font-semibold text-slate-900">
                <MessageCircle className="h-4 w-4" />
                미리보기
              </div>
              <dl className="mb-3 space-y-1 text-xs text-slate-600">
                <div className="flex justify-between gap-3"><dt>발송시간</dt><dd className="text-right text-slate-900">{scheduledAt || '-'}</dd></div>
                <div className="flex justify-between gap-3"><dt>대상</dt><dd className="text-right text-slate-900">{targetMode === TARGET_ALL_KAKAO ? '수신동의 전체' : `${selected.length.toLocaleString()}명`}</dd></div>
              </dl>
              <div className="max-h-[360px] overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-3 text-slate-700">
                {previewText || '입력된 변수가 없습니다.'}
              </div>
            </div>
          </aside>
        </form>
      ) : (
        <div className="rounded border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={historyStatus}
                onChange={(e) => {
                  setHistoryStatus(e.target.value)
                  setHistoryPage(0)
                }}
                className="rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
              >
                {STATUS_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
              <button
                type="button"
                onClick={() => loadHistory()}
                className="inline-flex items-center gap-2 rounded border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <RefreshCw className={`h-4 w-4 ${historyLoading ? 'animate-spin' : ''}`} />
                새로고침
              </button>
            </div>
            <div className="text-sm text-slate-500">총 {Number(historyResult.total || 0).toLocaleString()}건</div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">상태</th>
                  <th className="px-4 py-3">템플릿</th>
                  <th className="px-4 py-3">발송시간</th>
                  <th className="px-4 py-3">대상</th>
                  <th className="px-4 py-3">결과</th>
                  <th className="px-4 py-3 text-right">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {historyLoading ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">불러오는 중</td></tr>
                ) : historyResult.items.length ? historyResult.items.map((row) => (
                  <tr key={row.id} className="align-top">
                    <td className="px-4 py-3">
                      <span className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold ${statusClass(row.status)}`}>
                        {row.statusLabel || row.status || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{row.title || row.templateName || '-'}</div>
                      <div className="mt-0.5 font-mono text-xs text-slate-500">{row.templateCode || '-'}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-700">{formatMenuInstant(row, 'scheduledAt')}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-700">{Number(row.recipientCount || 0).toLocaleString()}명</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                      {Number(row.successCount || 0).toLocaleString()} / {Number(row.failureCount || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openDetail(row)}
                          className="inline-flex items-center gap-1 rounded border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          상세
                        </button>
                        {row.status === 'SCHEDULED' ? (
                          <button
                            type="button"
                            onClick={() => cancelSchedule(row)}
                            disabled={cancellingId === row.id}
                            className="inline-flex items-center gap-1 rounded border border-rose-200 px-2.5 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                          >
                            <Ban className="h-3.5 w-3.5" />
                            취소
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">예약발송 이력이 없습니다.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-200 p-4 text-sm">
            <button
              type="button"
              disabled={historyPage <= 0}
              onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
              className="rounded border border-slate-300 px-3 py-1.5 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              이전
            </button>
            <span className="text-slate-500">{historyPage + 1} / {Math.max(1, historyResult.totalPages || 1)}</span>
            <button
              type="button"
              disabled={historyPage + 1 >= Math.max(1, historyResult.totalPages || 1)}
              onClick={() => setHistoryPage((p) => p + 1)}
              className="rounded border border-slate-300 px-3 py-1.5 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              다음
            </button>
          </div>
        </div>
      )}

      {(detailRow || detailLoading) ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <div className="font-semibold text-slate-900">예약발송 상세</div>
                {detailRow ? <div className="mt-0.5 text-xs text-slate-500">#{detailRow.id} · {detailRow.statusLabel || detailRow.status}</div> : null}
              </div>
              <button type="button" onClick={() => setDetailRow(null)} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            {detailLoading || !detailRow ? (
              <div className="px-5 py-12 text-center text-sm text-slate-500">불러오는 중</div>
            ) : (
              <div className="max-h-[calc(90vh-72px)] overflow-auto p-5">
                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="rounded border border-slate-200 p-4">
                    <div className="mb-3 font-semibold text-slate-900">예약 정보</div>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between gap-3"><dt className="text-slate-500">템플릿</dt><dd className="text-right text-slate-900">{detailRow.templateName || detailRow.templateCode}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-slate-500">발송시간</dt><dd className="text-right text-slate-900">{formatMenuInstant(detailRow, 'scheduledAt')}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-slate-500">수신대상</dt><dd className="text-right text-slate-900">{Number(detailRow.recipientCount || 0).toLocaleString()}명</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-slate-500">성공/실패</dt><dd className="text-right text-slate-900">{Number(detailRow.successCount || 0).toLocaleString()} / {Number(detailRow.failureCount || 0).toLocaleString()}</dd></div>
                    </dl>
                  </div>
                  <div className="rounded border border-slate-200 p-4">
                    <div className="mb-3 font-semibold text-slate-900">발송 내용</div>
                    <div className="max-h-64 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-3 text-sm text-slate-700">
                      {detailRow.messagePreview || '-'}
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <div className="rounded border border-slate-200 p-4">
                    <div className="mb-3 font-semibold text-slate-900">변수</div>
                    <div className="max-h-72 overflow-auto divide-y divide-slate-100 text-sm">
                      {Object.entries(detailRow.variables || {}).length ? Object.entries(detailRow.variables || {}).map(([key, value]) => (
                        <div key={key} className="grid grid-cols-[140px_minmax(0,1fr)] gap-3 py-2">
                          <div className="break-all font-mono text-xs text-slate-500">{key}</div>
                          <div className="whitespace-pre-wrap break-words text-slate-800">{String(value)}</div>
                        </div>
                      )) : <div className="py-6 text-center text-sm text-slate-500">변수가 없습니다.</div>}
                    </div>
                  </div>
                  <div className="rounded border border-slate-200 p-4">
                    <div className="mb-3 font-semibold text-slate-900">수신대상</div>
                    <div className="max-h-72 overflow-auto divide-y divide-slate-100 text-sm">
                      {Array.isArray(detailRow.recipients) && detailRow.recipients.length ? detailRow.recipients.map((item) => (
                        <div key={item.userId || `${item.email}-${item.phoneMasked}`} className="flex items-center justify-between gap-3 py-2">
                          <div className="min-w-0">
                            <div className="truncate font-semibold text-slate-900">{item.name || item.email}</div>
                            <div className="truncate text-xs text-slate-500">{item.orgName || '-'} · {item.email || '-'}</div>
                          </div>
                          <div className="shrink-0 text-xs font-semibold text-slate-600">{item.phoneMasked || '-'}</div>
                        </div>
                      )) : <div className="py-6 text-center text-sm text-slate-500">수신대상이 없습니다.</div>}
                    </div>
                  </div>
                </div>

                {Array.isArray(detailRow.failureSamples) && detailRow.failureSamples.length ? (
                  <div className="mt-4 rounded border border-rose-100 bg-rose-50 p-4 text-sm text-rose-800">
                    <div className="mb-2 font-semibold">실패 샘플</div>
                    {detailRow.failureSamples.map((item) => (
                      <div key={`${item.userId}-${item.errorCode}`}>{item.name || item.userId} · {item.phoneMasked || '-'} · {item.errorCode || '-'}</div>
                    ))}
                  </div>
                ) : null}

                {detailRow.status === 'SCHEDULED' ? (
                  <div className="mt-5 flex justify-end">
                    <button
                      type="button"
                      onClick={() => cancelSchedule(detailRow)}
                      disabled={cancellingId === detailRow.id}
                      className="inline-flex items-center gap-2 rounded border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                    >
                      <Ban className="h-4 w-4" />
                      예약발송 취소
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </section>
  )
}
